import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
    Award, Search, RefreshCw, AlertCircle, Crown, 
    Star, TrendingUp
} from 'lucide-react';
import { rangoService } from '../../services/rangoService';
import { useNavigate } from 'react-router-dom';

const Rangos = () => {
    const [rangos, setRangos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
 const initialLoad = useRef(false);


    const cargarRangos = async () => {
        setLoading(true);
        setError('');
        const result = await rangoService.obtenerRangos();
        
        if (result.success) {
            setRangos(result.data);
        } else {
            setError(result.error);
            if (result.code === 401 || result.code === 403) {
                setTimeout(() => navigate('/login'), 2000);
            }
        }
        setLoading(false);
    };

    useEffect(() => {
    if (initialLoad.current) return;
    initialLoad.current = true;
    cargarRangos();
}, []);

    const rangosFiltrados = useMemo(() => {
        if (!searchTerm) return rangos;
        const term = searchTerm.toLowerCase();
        return rangos.filter(r => 
            r.nombre?.toLowerCase().includes(term) ||
            r.codigo?.toLowerCase().includes(term) ||
            r.condicion?.toLowerCase().includes(term)
        );
    }, [rangos, searchTerm]);

    const getRankStyle = (nombre) => {
        if (nombre.includes('Embajador') || nombre.includes('Corona')) {
            return {
                badge: 'from-purple-500 to-purple-600',
                iconBg: 'bg-purple-50',
                iconColor: 'text-purple-600',
                levelColor: 'text-purple-700'
            };
        }
        if (nombre.includes('Diamante')) {
            return {
                badge: 'from-blue-500 to-blue-600',
                iconBg: 'bg-blue-50',
                iconColor: 'text-blue-600',
                levelColor: 'text-blue-700'
            };
        }
        if (nombre.includes('Estrella')) {
            return {
                badge: 'from-amber-500 to-amber-600',
                iconBg: 'bg-amber-50',
                iconColor: 'text-amber-600',
                levelColor: 'text-amber-700'
            };
        }
        return {
            badge: 'from-gray-500 to-gray-600',
            iconBg: 'bg-gray-50',
            iconColor: 'text-gray-600',
            levelColor: 'text-gray-700'
        };
    };

    const getIcon = (nombre) => {
        if (nombre.includes('Embajador') || nombre.includes('Corona')) return <Crown className="w-3.5 h-3.5" />;
        if (nombre.includes('Diamante')) return <Star className="w-3.5 h-3.5" />;
        return <Award className="w-3.5 h-3.5" />;
    };

    return (
        <div className="p-4 lg:p-6 w-full max-w-[1600px] mx-auto">
            {/* Encabezado */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/20">
                            <TrendingUp className="w-6 h-6 text-white" />
                        </div>
                        Rangos DXN
                    </h1>
                    <p className="text-gray-500 text-sm mt-1 ml-11">Plan de compensación y jerarquía</p>
                </div>
                
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-none">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Buscar rango..." 
                            value={searchTerm} 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full sm:w-72 pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all shadow-sm"
                        />
                    </div>
                    <button 
                        onClick={cargarRangos} 
                        disabled={loading} 
                        className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/20 disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse space-y-3">
                            <div className="flex gap-2">
                                <div className="h-5 bg-gray-100 rounded w-16"></div>
                                <div className="h-5 bg-gray-100 rounded w-20"></div>
                            </div>
                            <div className="h-4 bg-gray-100 rounded w-3/4"></div>
                            <div className="space-y-2">
                                <div className="h-3 bg-gray-100 rounded w-full"></div>
                                <div className="h-3 bg-gray-100 rounded w-5/6"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : rangosFiltrados.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm font-medium">No se encontraron rangos</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
                    {rangosFiltrados.map((rango) => {
                        const style = getRankStyle(rango.nombre);
                        return (
                            <motion.div
                                key={rango.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3 }}
                                whileHover={{ y: -2 }}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300 overflow-hidden group"
                            >
                                {/* Header con gradiente sutil */}
                                <div className="relative p-4 bg-gradient-to-br from-blue-50 via-white to-purple-50 border-b border-gray-100">
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className={`px-2 py-1 bg-gradient-to-r ${style.badge} text-white text-[10px] font-bold rounded-md shadow-sm`}>
                                                    Nivel {rango.orden}
                                                </span>
                                                {rango.codigo && (
                                                    <span className="text-[10px] font-mono text-gray-600 bg-white px-2 py-1 rounded-md border border-gray-200 shadow-sm">
                                                        {rango.codigo}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-bold text-gray-900 text-sm leading-tight line-clamp-2" title={rango.nombre}>
                                                {rango.nombre}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                {/* Cuerpo */}
                                <div className="p-4">
                                    <div className="flex items-start gap-3">
                                        <div className={`p-1.5 ${style.iconBg} rounded-lg shrink-0`}>
                                            <span className={style.iconColor}>
                                                {getIcon(rango.nombre)}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-600 leading-relaxed">
                                                {rango.condicion}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Rangos;